/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AssessmentAttachments } from 'app/core/components/assessments/assessment-attachments';
import { AuthChangeEvent } from 'app/um/lib/events';
import { ApiQuery, apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import * as UM from 'app/um/models/essette-api';


const ATTACHMENT_ROUTE: string = 'um.auth.assessment';

@autoinject()
export class AuthAssessmentsPage extends ComponentBase {

    ////****************** Fields and Properties *************F***********************////
    protected authId!: string;
    private authSubClassId: string | null = null;
    protected readonly: boolean = false;
    protected config: IAssessmentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        generateAttachmentRoute: this.generateAuthRoute.bind(this),
        assessmentsApiFilter: this.addAssessmentsFilter.bind(this),
        addTargetProperty: this.addAuthProperty.bind(this)
    }

    protected hasOpenAttachment: boolean = false;

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $attachments!: AssessmentAttachments;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        private readonly authService: AuthService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        return true;
    }

    protected async activate(params: { authId: string }): Promise<void> {
        if(this.authId !== params.authId) { this.authId = params.authId; }
        await this.checkPermissions();
        this.authSubClassId = this.context.auth.class?.subClass?.id || null;

        /** Initialize action includes adding any assessments required by the auth's sub-class (if they don't exist) */
        await this.initializeAuth(this.authId);

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => this.handleAuthChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleAuthChangeEvent(e: AuthChangeEvent) {
        if(e.authReloaded) {
            this.checkPermissions();
            this.$attachments.reload();
        };
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasOpenAttachment = this.accountContext.hasRouteAccess(ATTACHMENT_ROUTE);
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
    }

    public generateAuthRoute(targetId: string, attachmentId: string): string | null {
        if(!this.hasOpenAttachment) { return null; }
        return this.router.generate(ATTACHMENT_ROUTE, { authId: targetId, attachmentId: attachmentId });
    }

    public addAuthProperty(attachment: UM.AuthAssessmentAttachment): void {
        attachment.auth = { id: this.authId };
    }

    public addAssessmentsFilter(query: ApiQuery): void {
        const authDate = this.context.auth.admission?.date || this.context.auth.serviceDate?.dateRange?.start || this.context.auth.initialDateOfService || this.context.auth.requestedDateTime || this.env.today;

        if(this.authSubClassId) {
            query.filterExists('subclasses', ((f) => {
                f.filterIdentifier('id', this.authSubClassId!);
                if(authDate.length === 10) {
                    f.filterDate('detail.dateRange.start', authDate, 'lte');
                } else {
                    f.filterDateTime('detail.dateRange.start', authDate, 'lte');
                }
                f.filterOr((or) => {
                    or.filterIsNull('detail.dateRange.end', true);
                    if(authDate.length === 10) {
                        or.filterDate('detail.dateRange.end', authDate, 'gt');
                    } else {
                        or.filterDateTime('detail.dateRange.end', authDate, 'gt');
                    }
                });
            }));
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async initializeAuth(authId: string): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.authService.initializeAuth(authId);
            return response.isSuccessful;
        } catch(error) {
            this.logger.error('Error calling initialize action on auth');
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
